// An aircraft as a SkyDemon ".aircraft" file. SkyDemon publishes no schema;
// the attributes below are those of an export from SkyDemon itself (the
// katana-dv20 example in rdamazio/efis-editor#305), metric: litres,
// kilograms, lever arms and the CG envelope in millimetres from the datum.
// Only what an aircraft profile holds is written; SkyDemon's climb, descent
// and checklist data are not, because the club does not keep them.
//
// Pure; see aircraftProfile.ts.

import type { AircraftProfile } from './aircraftProfile'
import { LITRES_PER_USG } from './fuelPlan'
import { toKnots } from './navigation'
import { seatRows } from './aircraftProfile'

const SKYDEMON_FUEL = { AVGAS: 'AvGas100LL', MOGAS: 'Mogas', DIESEL: 'Diesel' } as const

const esc = (text: string) =>
  text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
const num = (value: number, digits = 2) => value.toFixed(digits)
const mm = (metres: number) => num(metres * 1000)
const litres = (usg: number) => num(usg * LITRES_PER_USG)
// The manual's wording ("60", "60-70 (54 lyhyt rata)"): its first number.
const firstNumber = (text: string | null | undefined) => {
  const match = text?.match(/\d+(?:[.,]\d+)?/)
  return match ? Number(match[0].replace(',', '.')) : null
}

export function skydemonAircraftXml(profile: AircraftProfile): string {
  const { aircraft, performance: p } = profile
  const attrs: [string, string][] = [
    ['AirframeType', 'Aeroplane'],
    ['Name', aircraft.aircraftType ?? aircraft.registration],
    ['Registration', aircraft.registration],
    ['FuelType', SKYDEMON_FUEL[aircraft.fuelType]],
    ['FuelMeasurementType', 'Volume'],
    ['FuelMeasurementVolumeType', 'Litres'],
    ['FuelMeasurementMassType', 'Kilograms'],
    ['TaxiFuel', num((p.taxiLitresPerTenMin * p.taxiMinutes) / 10, 6)],
    ['MaxFuel', num((p.maxFuelMainUsg + p.maxFuelResUsg) * LITRES_PER_USG, 6)],
    ['WeightBalanceType', 'Standard'],
    ['EmptyWeight', num(p.emptyWeightKg, 6)],
    ['EmptyArmLon', mm(p.armBasicMass)],
    ['EmptyArmLat', '0.00'],
    ['MinimumArmLat', '0.00'],
    ['MaximumArmLat', '0.00'],
    ['EnvelopeType', 'DatumDistance'],
    ['Envelope', p.cgEnvelope.map((point) => `${mm(point.arm)},${num(point.kg, 6)}`).join(',')],
  ]
  if (aircraft.mtowKg) attrs.splice(attrs.findIndex(([k]) => k === 'EmptyWeight') + 1, 0, ['MaximumWeight', num(aircraft.mtowKg, 6)])
  const runway = firstNumber(p.runwayRequirement)
  if (runway) attrs.push(['MinimumRunwayLengthMetres', String(runway)])
  const glide = firstNumber(p.bestGlideSpeed)
  if (glide) attrs.push(['GlideAirspeed', num(toKnots(glide, p.speedUnit))])

  const points: string[] = []
  const fuel = (name: string, arm: number, usg: number) =>
    `    <FuelLoadingPoint Name="${name}" LeverArm="${mm(arm)}" LeverArmLat="0.00" DefaultValue="0.00" Capacity="${litres(usg + p.unusableFuelUsg)}" Unusable="${litres(p.unusableFuelUsg)}" />`
  if (p.maxFuelMainUsg > 0) points.push(fuel('Tank', p.armFuelMain, p.maxFuelMainUsg))
  if (p.maxFuelResUsg > 0) points.push(fuel('Reserve Tank', p.armFuelRes, p.maxFuelResUsg))
  const load = (name: string, arm: number) => `    <LoadingPoint Name="${esc(name)}" LeverArm="${mm(arm)}" LeverArmLat="0.00" DefaultValue="0.00" />`
  points.push(load('Pilot', p.armFrontPax), load('Front Passenger', p.armFrontPax))
  if (seatRows(p) === 2) points.push(load('Rear Passenger 1', p.armRearPax), load('Rear Passenger 2', p.armRearPax))
  if (p.baggageAreas.length === 0) points.push(load('Baggage', p.armCargo))
  for (const area of p.baggageAreas) points.push(load(`Baggage ${area.label}`, area.arm))

  const cruise = firstNumber(String(p.manualCruiseSpeed ?? p.cruiseSpeed))
  const burn = aircraft.fuelConsumptionUsg ? aircraft.fuelConsumptionUsg * LITRES_PER_USG : null
  const cruiseXml =
    cruise && burn
      ? `  <CruiseProfiles>\n    <CruiseProfile Name="Cruise" AirspeedType="True" FuelBurn="${num(burn, 6)}" IndicatedAirspeed="${num(toKnots(cruise, p.speedUnit), 6)}" Airspeed="${num(toKnots(cruise, p.speedUnit), 6)}" />\n  </CruiseProfiles>\n`
      : ''

  const open = attrs.map(([k, v]) => `${k}="${esc(v)}"`).join(' ')
  return `<?xml version="1.0" encoding="utf-8"?>\n<Aircraft ${open}>\n${cruiseXml}  <LoadingPoints>\n${points.join('\n')}\n  </LoadingPoints>\n</Aircraft>\n`
}
